/****************************************************************************
 * COPYRIGHT (c) 2025 Harman Connected Services
 *
 * All rights reserved
 *
 * This software embodies materials and concepts which are
 * confidential to Ignite Product and is made available solely
 * pursuant to the terms of a written license agreement with
 * Ignite Product.
 *
 * Designed and Developed by Harman International Industries, Inc.
 ***************************************************************************/
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import APIManager from "../../../Services/ApiManager/ApiManager";
import { IconButton, Typography, Tooltip, Box } from "@mui/material";
import "../../OEM-Admin-Components/DetailsPopUp/DetailsPopup.css";
import Grid from "@mui/material/Grid2";
import styles from "../../../page/ApplicationList/ApplicationList.module.css";
import { ClearIcon } from "@mui/x-date-pickers";
import { styled } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TablePagination from "@mui/material/TablePagination";
import Paper from "@mui/material/Paper";

function GlobalTaxTablePopUp({ onClose, row }) {
  const [productDetails, setProductDetails] = React.useState({});
  const [prices, setPrices] = useState([]);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);

  const frequencyUnitConverter = {
    "1-DAY": "Daily",
    "1-MONTH": "Monthly",
    "3-MONTH": "Quarterly",
    "6-MONTH": "Half yearly",
    "1-YEAR": "Yearly",
  };

  const extractPricingArray = (planDetails) => {
    if (
      planDetails.oneTimePlan?.planPrices?.length > 0
    ) {
      return planDetails.oneTimePlan.planPrices;
    } else if (
      planDetails.recurringPlans?.[0]?.planPrices?.length > 0
    ) {
      return planDetails.recurringPlans[0].planPrices;
    }
    return [];
  };

  const createDataPrice = (id, country, countryCode, currencyCode, price, taxRate, taxRateinPercent) => {
    return {
      id,
      country,
      countryCode,
      currencyCode,
      price,
      taxRate,
      taxRateinPercent,
    };
  };

  const mapPricesToRows = (pricingArray) => {
    return pricingArray.map((price) =>
      createDataPrice(
        price.id,
        price.country,
        price.countryCode,
        price.currencyCode,
        price.price.toFixed(2),
        price.taxRate.toFixed(2),
        price.taxRateinPercent
      )
    );
  };

  useEffect(() => {
    if (row?.productId && row?.planId) {
      // Use getPlanDetails API for Global Admin
      APIManager.getPlanDetails(row.productId, row.planId).then((json) => {
        setProductDetails(json);
        const pricingArr = extractPricingArray(json);
        const allRows = mapPricesToRows(pricingArr);
        setPrices(allRows);
      }).catch((error) => {
        console.error("Error fetching plan details:", error);
      });
    }
  }, [row]);

  const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
      backgroundColor: theme.palette.action.hover,
      fontWeight: "600",
      border: 0,
      paddingTop: 8,
      paddingBottom: 8,
      borderBottom: "1px solid #d0d0d0 !important",
      fontSize: 12,
    },
    [`&.${tableCellClasses.body}`]: {
      fontSize: 14,
    },
  }));

  const StyledTableRow = styled(TableRow)(({ theme }) => ({
    "td, th": {
      border: 0,
      paddingTop: 5,
      paddingBottom: 5,
      borderBottom: "1px solid #e8e8e8",
    },
  }));

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10));
    setPage(0);
  };

  const visibleRows = React.useMemo(
    () =>
      [...prices].slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [page, rowsPerPage, prices]
  );

  return (
    <>
      <div className={styles.modalHeaderCls}>
        <Typography
          variant="subtitle1"
          gutterBottom
          sx={{ fontWeight: "500", fontSize: "20px" }}
        >
          In-App Products Pricings          
        </Typography>
        <IconButton>
          <ClearIcon onClick={onClose} sx={{ padding: "none" }} />
        </IconButton>
      </div>
      <div style={{marginTop: "3%"}}>
        <div>
          {productDetails?.product && (
            <Grid container spacing={2}>
              <Grid size={4} sx={{ overflow: 'hidden' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', maxWidth: '100%' }}>
                  <Typography variant="body2" sx={{ fontWeight: 500, color: '#666', flexShrink: 0 }}>
                    Plan Name:
                  </Typography>
                  <Tooltip 
                    title={productDetails.recurringPlans?.[0]?.name || productDetails.oneTimePlan?.name || ''} 
                    arrow
                    placement="top"
                  >
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        marginLeft: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        cursor: 'pointer',
                        minWidth: 0,
                        flex: 1,
                        fontWeight: 600
                      }}
                    >
                      {productDetails.recurringPlans?.[0]?.name || productDetails.oneTimePlan?.name}
                    </Typography>
                  </Tooltip>
                </Box>
              </Grid>
              <Grid size={4}>
                <Typography variant="body2" sx={{ display: 'inline', fontWeight: 500, color: '#666' }}>
                  Recurring Price:
                </Typography>
                <Typography variant="body2" sx={{ display: 'inline', marginLeft: 1, fontWeight: 600 }}>
                  {(productDetails.recurringPlans?.[0]?.basePrice?.price || productDetails.oneTimePlan?.basePrice?.price)?.toFixed(2)} {productDetails.recurringPlans?.[0]?.basePrice?.currencyCode || productDetails.oneTimePlan?.basePrice?.currencyCode}
                </Typography>
              </Grid>
              <Grid size={4}>
                <Typography variant="body2" sx={{ display: 'inline', fontWeight: 500, color: '#666' }}>
                  Recurring Frequency:
                </Typography>
                <Typography variant="body2" sx={{ display: 'inline', marginLeft: 1, fontWeight: 600 }}>
                  {frequencyUnitConverter[
                    productDetails.recurringPlans?.[0]?.recurringFrequency +
                      "-" +
                      productDetails.recurringPlans?.[0]?.recurringFrequencyUnit
                  ] || 'N/A'}
                </Typography>
              </Grid>
              {productDetails.recurringPlans?.[0] && (
                <>
                  <Grid size={4}>
                    <Typography variant="body2" sx={{ display: 'inline', fontWeight: 500, color: '#666' }}>
                      When to Charge:
                    </Typography>
                    <Typography variant="body2" sx={{ display: 'inline', marginLeft: 1, fontWeight: 600 }}>
                      {productDetails.recurringPlans[0]?.chargeType === 'IMMEDIATE'
                        ? "Immediately"
                        : "End Of Billing Cycle"}
                    </Typography>
                  </Grid>
                  <Grid size={4}>
                    <Typography variant="body2" sx={{ display: 'inline', fontWeight: 500, color: '#666' }}>
                      Grace period:
                    </Typography>
                    <Typography variant="body2" sx={{ display: 'inline', marginLeft: 1, fontWeight: 600 }}>
                      {productDetails.recurringPlans[0]?.gracePeriod} Days
                    </Typography>
                  </Grid>
                  <Grid size={4}>
                    <Typography variant="body2" sx={{ display: 'inline', fontWeight: 500, color: '#666' }}>
                      Auto Renew:
                    </Typography>
                    <Typography variant="body2" sx={{ display: 'inline', marginLeft: 1, fontWeight: 600 }}>
                      {productDetails.recurringPlans[0]?.autoRenew
                        ? "Disabled"
                        : "Enabled"}
                    </Typography>
                  </Grid>
                </>
              )}
            </Grid>
          )}
            
          <div>
            <Typography variant="subtitle2" gutterBottom fontWeight={"bold"} sx={{marginTop: "2%"}}>
              Country specific price settings
            </Typography>
            {visibleRows.length > 0 && (
              <Box sx={{ width: "100%", marginTop: "3%" }}>
                <Paper sx={{ width: "100%", mb: 2 }} elevation={0}>
                  <TableContainer>
                    <Table aria-label="customized table">
                      <TableHead>
                        <TableRow>
                          <StyledTableCell>Country</StyledTableCell>
                          <StyledTableCell>Price</StyledTableCell>
                          <StyledTableCell>Tax Rate</StyledTableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {visibleRows.map((price) => (
                          <StyledTableRow key={price.id}>
                            <StyledTableCell sx={{ fontWeight: "bold", width: "40%" }}>
                              {price.country}
                            </StyledTableCell>
                            <StyledTableCell sx={{ width: "30%" }}>
                              <span style={{ color: "#717273", marginRight: "10%" }}>
                                {price.currencyCode}
                              </span>
                              {price.price}
                            </StyledTableCell>
                            <StyledTableCell sx={{ width: "30%" }}>
                              {price.taxRateinPercent}% ({price.currencyCode} {price.taxRate})
                            </StyledTableCell>
                          </StyledTableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={prices.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    labelRowsPerPage="Items per page"
                  />
                </Paper>
              </Box>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

GlobalTaxTablePopUp.propTypes = {
  onClose: PropTypes.func.isRequired,
  row: PropTypes.shape({
    productId: PropTypes.string,
    planId: PropTypes.string,
  }).isRequired,
};

export default GlobalTaxTablePopUp;